import React, { useEffect, useMemo, useRef, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useReduxSelector } from "@hooks/useReduxSelector";
import { salaryEvolutionActions } from "@store/actions";
import { Line } from "react-chartjs-2";
import { Controller, useForm } from "react-hook-form";

import DateRangePickerDropdown from "@components/dateRange/DropdownDateRange";
import { DropdownDevise, DropdownYear } from "@components/tailwindComponents";
import Button from "@components/tailwindComponents/Button";
import { customTooltipsEvolution } from "@pages/entreprise/employeTab/container/statistic/Graphs/SalaryEvolution/customOptions";

import {
  CONSTANT_SOCKET_FOR_COURSE,
  INIT_EVOLUTION
} from "@helpers/_constants";
import { reactMemo } from "@helpers/retyped-functions";

const _initPeriod = [
  {
    startDate: moment().subtract(5, "years").startOf("year").toDate(),
    endDate: moment().endOf("day").toDate()
  }
];

function SalaryEvolutionGraph(props) {
  
  const { t: translator } = useTranslation(
    "pages.employés.parcours&Certifications"
  );

  const { control, watch, setValue } = useForm({
    reValidateMode: "onChange",
    mode: "onChange"
  });
  const [devise, year, years] = watch(["devise", "year", "years"]);

  const idEnterprise = useReduxSelector(
    ({ userConnected }) => userConnected.Enterprise
  );

  const socket = useReduxSelector(({ abstract }) => abstract.socket);
  
  const salaryEvolutions = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.salaryEvolutions
  );
  const isCurrentEmployee = !props.loadOnMount || (
    salaryEvolutions.filter?.employers?.length === 1 &&
    salaryEvolutions.filter.employers[0] === props.employeeId);
  const { dataMonth, dataYears } = isCurrentEmployee ? salaryEvolutions : {};
  const [selectedType, setSelectedType] = useState("years");


  const chartRef = useRef(null);
  const LINE_GRAPH_OPTIONS = {
    scales: {
      yAxes: [
        {
          ticks: {
            suggestedMin: 0, // Valeur minimale de l'intervalle de l'axe Y
            //suggestedMax: 10, // Valeur maximale de l'intervalle de l'axe Y
            stepSize: 10000 // Pas de l'axe Y
          }
        }
      ]
    },
    legend: {
      display: false
    },
    spanGaps: true,
    tooltips: {
      enabled: false,
      custom: (tooltip) =>
        customTooltipsEvolution(tooltip, chartRef, translator)
    }
  };

  const lists =
    useMemo(() => {
      if (selectedType === "month") {
        return dataMonth?.data?.map((dataSet) => ({
          ...dataSet,
          data: dataSet.data?.map((_data) => ({
            x: translator(_data.x),
            y: _data.y
          }))
        }));
      }

      return dataYears?.data;
    }, [dataMonth, dataYears, selectedType]) || [];

  const dataLabels = useMemo(
    () =>
      selectedType === "month" && dataMonth?.labels?.length
        ? dataMonth?.labels.map((_month) => translator(_month))
        : selectedType === "years" && dataYears?.labels?.length
          ? dataYears?.labels
          : [],
    [dataMonth, dataYears, selectedType]
  );

  const lineGraphData = useMemo(
    () => ({
      labels: [...dataLabels],
      datasets: [...lists]
    }),
    [dataLabels, lists]
  );
  const initDevise = props?.deviseList?.find(
    (_devise) => _devise._id === lineGraphData?.datasets?.[0]?.devise
  );

  const dispatch = useDispatch();

  const handleChangeDevise = (_devise) => {
    dispatch(
      salaryEvolutionActions.filterData({
        devise: _devise,
        year: year._id,
        years: years?.[0]
          ? [
              new Date(years?.[0]?.startDate).getFullYear(),
              new Date(years?.[0]?.endDate).getFullYear()
            ]
          : [...INIT_EVOLUTION],
        filter: {
          employers: [props?.employeeId]
        }
      })
    );
  };

  const handleChangeYear = (_year) => {
    dispatch(
      salaryEvolutionActions.filterData({
        devise: devise._id,
        year: _year,
        years: years?.[0]
          ? [
              new Date(years?.[0]?.startDate).getFullYear(),
              new Date(years?.[0]?.endDate).getFullYear()
            ]
          : [...INIT_EVOLUTION],
        filter: {
          employers: [props?.employeeId]
        }
      })
    );
  };
  const handleChangeYears = (_years) => {
    if (_years) {
      dispatch(
        salaryEvolutionActions.filterData({
          devise: devise._id,
          year: year._id,
          years: _years,
          filter: {
            employers: [props?.employeeId]
          }
        })
      );
    }
  };
  useEffect(() => {
    if (!socket) return;
    const handleCourse = ({ Enterprise, Employer } = {}) => {
      if (
        Enterprise && Employer &&
        idEnterprise?.toString() === Enterprise.toString() &&
        Employer.toString() === props.employeeId?.toString()
      ) {
        setValue("years", _initPeriod);
      }
    };
    socket.on(CONSTANT_SOCKET_FOR_COURSE, handleCourse);
    return () => socket.off(CONSTANT_SOCKET_FOR_COURSE, handleCourse);
  }, [socket, idEnterprise, props.employeeId, setValue]);
  
  useEffect(() => {
    if (!props.loadOnMount) return;
    dispatch(salaryEvolutionActions.filterData({
      devise: props.deviseList?.[0]?._id,
      year: new Date().getFullYear(),
      years: [...INIT_EVOLUTION],
      filter: { employers: [props.employeeId] }
    }));
    // This component stays mounted when its section/tab is hidden.
  }, [dispatch, props.employeeId, props.loadOnMount]);

  return (
    <>
      <div className="arh-mt-20 arh-flex arh-w-full arh-items-center arh-justify-between">
        <div className="arh-flex arh-items-center arh-space-x-4">
          <div className="arh-font-poppins arh-text-15 arh-font-bold arh-text-black">
            {translator("Évolution salariale")}
          </div>
          <div className="arh-flex arh-items-center arh-justify-center arh-space-x-4">
            <div className="arh-w-24">
              <DropdownDevise
                control={control}
                customButtonClass="arh-py-3"
                defaultValue={initDevise}
                setSelectedDeviseEvolution={handleChangeDevise}
              />
            </div>
            {selectedType === "month" ? (
              <div className="arh-w-24">
                <DropdownYear
                  control={control}
                  name="year"
                  setSelectedYearEvolution={handleChangeYear}
                />
              </div>
            ) : (
              <Controller
                control={control}
                name="years"
                defaultValue={_initPeriod}
                rules={{
                  required: true,
                  validate: (period) =>
                    Boolean(period?.[0]?.startDate && period?.[0]?.endDate)
                }}
                render={({
                  field: { onChange, value, name },
                  fieldState: { invalid }
                }) => (
                  <div className="datepicker_modals_parcours datePicker_posteAvailable blackSvgColor arh-year-range ">
                    <div className="dateRange_green position_relative DateRangePicker_customisation arh-year-range-green DateRangePicker_customisation--green">
                      <DateRangePickerDropdown
                        key="date_range_periode"
                        classParent="arhDatePicker"
                        className="input_simple focus_green"
                        textInput={translator("Années")}
                        classSwitch="c-switch-pill-checked--green--color--to--left"
                        classBackground="green"
                        SaveCloseRange={(date) => {
                          onChange(date);
                          if (date?.[0].startDate && date?.[0].endDate)
                            handleChangeYears([
                              new Date(date?.[0].startDate).getFullYear(),
                              new Date(date?.[0].endDate).getFullYear()
                            ]);
                        }}
                        dateSelected={value?.[0]}
                        dateFormat="yyyy"
                        showMonthDropdown={false}
                        showYearDropdown={false}
                        showResetFilter={false}
                        showYearPicker
                        yearRange
                        cancelMode
                      />
                    </div>
                  </div>
                )}
              />
            )}
          </div>
        </div>
        <div className="arh-flex arh-items-center arh-justify-center arh-space-x-2">
          <Button
            variant={selectedType === "years" ? "green" : "green-outlined"}
            onClick={() => setSelectedType("years")}
          >
            {translator("Vue années")}
          </Button>

          <Button
            onClick={() => setSelectedType("month")}
            variant={selectedType === "month" ? "green" : "green-outlined"}
          >
            {translator("Vue mois")}
          </Button>
        </div>
      </div>
      <div className="arh-detail-canvasSvg arh-mt-9">
        <Line
          ref={chartRef}
          options={LINE_GRAPH_OPTIONS}
          data={lineGraphData}
        />
      </div>
    </>
  );
}

export default reactMemo(SalaryEvolutionGraph);
